import { RowNetworkKey } from "../ids/row-network-key.ts";
/**
 * Trailing-debounce for BACKEND cell persists.
 *
 * The local store write + formula recompute always happen immediately (live UX);
 * only the network persist (`backend.writeCell` / `backend.clearCell`) is coalesced
 * per `(rowKey, date)` so a burst of keystrokes is ONE request, not N. An
 * immediate ("now") write — e.g. on blur / Enter — cancels any pending timer for
 * that cell and persists synchronously, so a flush is just a "now" write. This is
 * the seam that lets number cells commit live (mirroring text cells) without a
 * POST per keystroke.
 *
 * Errors from the deferred persist have no caller to surface them, so the
 * scheduled `run` MUST swallow its own errors (best-effort, like the rest of the
 * optimistic-local-first path).
 *
 * A pending persist dropped by `clear` never runs, so it is never answered:
 * each `schedule` names a `drop` that settles whoever waits on it (a durable
 * write's `settled` rejects), and `clear` calls it — a dropped write is never
 * left waiting forever.
 */
export class CellWriteCoalescer {
  readonly #pending = new Map<
    string,
    { timer: ReturnType<typeof setTimeout>; run: () => void; drop: () => void }
  >();

  readonly #delayMs: number;
  readonly #owners = new Map<string, object>();
  constructor(delayMs: number = 300) {
    this.#delayMs = delayMs;
  }

  /**
   * Stable Map key for a cell's pending persist, composed from `(rowKey, date)`.
   * `|` separates the two parts: it never occurs in a row key (`r_<hex>` or a
   * `:~…` canonical key) or in the `date:cellId` second arg, so distinct pairs
   * can't collide into one key. (`:` would be ambiguous — canonical row keys are
   * full of colons.) This key is internal to the coalescer: it's only compared
   * for equality, never parsed back apart.
   */
  static key(rowKey: RowNetworkKey, date: string): string {
    return `${rowKey}|${date}`;
  }

  /**
   * Schedule a trailing-debounced persist for `key`, replacing any pending
   * one. `drop` runs instead of `run` when `clear` drops the persist unsent.
   */
  schedule(key: string, run: () => void, drop: () => void): void {
    this.cancel(key);
    const timer = setTimeout(() => {
      this.#pending.delete(key);
      this.#owners.delete(key);
      run();
    }, this.#delayMs);
    this.#pending.set(key, { timer, run, drop });
  }

  /** Admit a trailing persist with its transport owner; replacing it transfers only this cell. */
  scheduleOwned(owner: object, key: string, run: () => void, drop: () => void): void {
    this.schedule(key, run, drop);
    this.#owners.set(key, owner);
  }

  /** Flush only operations admitted by this owner; other holders retain their debounce. */
  flushOwner(owner: object): void {
    for (const [key, held] of this.#owners) {
      if (held !== owner) continue;
      const entry = this.#pending.get(key);
      this.cancel(key);
      entry?.run();
    }
  }

  /** Cancel any pending persist for `key` — the caller is persisting
   *  immediately, and settles what waited on the cancelled one. */
  cancel(key: string): void {
    const entry = this.#pending.get(key);
    if (entry !== undefined) {
      clearTimeout(entry.timer);
      this.#pending.delete(key);
      this.#owners.delete(key);
    }
  }

  /** Run + clear every pending persist now (sheet teardown / page unload). */
  flushAll(): void {
    for (const [key, entry] of [...this.#pending]) {
      clearTimeout(entry.timer);
      this.#pending.delete(key);
      this.#owners.delete(key);
      entry.run();
    }
  }

  /** Drop every pending persist without running it (dispose without flush);
   *  each one's `drop` runs, so nothing waits on a persist that never ran. */
  clear(): void {
    const dropped = [...this.#pending.values()];
    this.#pending.clear();
    this.#owners.clear();
    for (const entry of dropped) {
      clearTimeout(entry.timer);
      entry.drop();
    }
  }
}
